import { Clock, X } from "lucide-react";
import React, { useMemo } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { parseTradableTimes, TradableTimeWindow } from "../types/contract";

interface TradableTimesModalProps {
  isOpen: boolean;
  onClose: () => void;
  tradableTimesUtc: string | null;
  symbol: string;
  timeZoneId: string | null;
}

const TradableTimesModal: React.FC<TradableTimesModalProps> = ({
  isOpen,
  onClose,
  tradableTimesUtc,
  symbol,
  timeZoneId,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const windows = useMemo(() => parseTradableTimes(tradableTimesUtc), [tradableTimesUtc]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    });
  };

  // Group windows by date for better display
  const groupedByDate = useMemo(() => {
    const groups: Record<string, TradableTimeWindow[]> = {};
    for (const window of windows) {
      const dateKey = window.startTime.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(window);
    }
    return groups;
  }, [windows]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Clock className={`w-5 h-5 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Tradable Times - {symbol}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors ${
              isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Timezone Info */}
        {timeZoneId && (
          <div
            className={`px-4 py-2 text-sm ${
              isDarkMode ? "bg-gray-700/50 text-gray-300" : "bg-gray-50 text-gray-600"
            }`}
          >
            Timezone: <span className="font-medium">{timeZoneId}</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {windows.length === 0 ? (
            <div
              className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No tradable times available
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedByDate).map(([dateKey, dayWindows]) => (
                <div key={dateKey}>
                  <h3
                    className={`text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {formatDate(dayWindows[0].startTime)}
                  </h3>
                  <div className="space-y-1">
                    {dayWindows.map((window, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded ${
                          isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded ${
                              isDarkMode
                                ? "bg-green-900/30 text-green-400"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            OPEN
                          </span>
                          <span
                            className={`text-sm font-mono ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {formatTime(window.startTime)}
                          </span>
                        </div>
                        <span className={`${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          →
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-mono ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {formatTime(window.endTime)}
                          </span>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded ${
                              isDarkMode
                                ? "bg-red-900/30 text-red-400"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            CLOSE
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-between p-4 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {windows.length} trading window{windows.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradableTimesModal;

